export const emptySupportCase = {
	title: "", description: "", status: "open", priority: "normal", type: "general",
	customerId: "", customerEmail: "", restaurantId: "", restaurantName: "", orderId: "", reservationId: "", assignedTo: "",
};

export const supportCaseForm = item => Object.fromEntries(Object.keys(emptySupportCase)
	.map(key => [key, item[key] ?? emptySupportCase[key]]));

export function supportCaseScope({ user, functions, auth, environment, selectedKey }) {
	const projectId = environment?.projectId;
	if (!user?.uid || user.uid !== auth.currentUser?.uid || !projectId || selectedKey !== environment.key ||
		projectId !== environment.firebaseConfig?.projectId || projectId !== functions.app?.options?.projectId ||
		projectId !== auth.app?.options?.projectId) {
		throw new Error("Support project or account changed. Reload and sign in again.");
	}
	return { projectId, actorUid: user.uid, environmentKey: environment.key };
}

export const sameSupportScope = (left, right) => Boolean(left && right && left.projectId === right.projectId &&
	left.actorUid === right.actorUid && left.environmentKey === right.environmentKey);

export function validateSupportCaseForm(form) {
	if (!form || Object.keys(form).some(key => !Object.hasOwn(emptySupportCase, key))) throw new Error("Invalid support case fields.");
	for (const key of Object.keys(emptySupportCase)) {
		const value = form[key];
		const max = key === "title" ? 180 : key === "description" ? 2000 : key === "restaurantName" ? 160 : key === "customerEmail" ? 254 : 128;
		// eslint-disable-next-line no-control-regex -- Reject non-text control characters without altering the submitted payload.
		if (typeof value !== "string" || value.length > max || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) {
			throw new Error(`Invalid ${key}. Maximum length: ${max}.`);
		}
	}
	if (!form.title.trim()) throw new Error("Support case title is required.");
	for (const [key, values] of Object.entries({ status: ["open", "waiting", "investigating", "resolved", "closed"],
		priority: ["low", "normal", "high", "urgent"], type: ["general", "billing", "refund", "reservation", "restaurant_ops", "account"] })) {
		if (!values.includes(form[key])) throw new Error(`Invalid ${key}.`);
	}
	for (const key of ["customerId", "restaurantId", "orderId", "reservationId", "assignedTo"]) {
		if (form[key] && (/[/\\\s]/.test(form[key]) || [".", ".."].includes(form[key]))) throw new Error(`Invalid ${key}.`);
	}
	if (form.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail.trim())) throw new Error("Invalid customer email.");
}

export function newSupportRequestId() {
	if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID();
	if (typeof window.crypto?.getRandomValues !== "function") throw new Error("Secure request IDs are unavailable.");
	return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");
}

const validCaseId = value => typeof value === "string" && value.length > 0 && value.length <= 128 &&
	! /[/\\\s]/.test(value) && value !== "." && value !== "..";

// One unresolved command per exact account/environment, retained across reloads.
export function supportCaseSubmissionStore(storage, scope) {
	const key = `scerv-support-case-pending-v1:${encodeURIComponent(JSON.stringify(scope))}`;
	const validate = pending => {
		const command = pending?.command;
		if (pending?.version !== 1 || !sameSupportScope(pending.scope, scope) || command?.projectId !== scope.projectId ||
			!/^[A-Za-z0-9_-]{16,128}$/.test(command.requestId || "") ||
			!Number.isSafeInteger(command.expectedRevision) || command.expectedRevision < 0 || command.expectedRevision >= Number.MAX_SAFE_INTEGER ||
			!(command.caseId === null || validCaseId(command.caseId)) ||
			(!command.caseId && command.expectedRevision !== 0) ||
			Object.keys(command).some(field => !["projectId", "requestId", "expectedRevision", "caseId", "supportCase"].includes(field))) {
			throw new Error("Pending support command scope is invalid. Recovery is required.");
		}
		validateSupportCaseForm(command.supportCase);
		return pending;
	};
	const read = () => {
		const raw = storage.getItem(key);
		if (raw === null) return null;
		let pending;
		try { pending = JSON.parse(raw); } catch (_) { throw new Error("Pending support command is unreadable. Recovery is required."); }
		return validate(pending);
	};
	const persist = command => {
		if (read()) throw new Error("A support command is still awaiting confirmation.");
		const pending = validate({ version: 1, scope, command });
		const raw = JSON.stringify(pending);
		storage.setItem(key, raw);
		if (storage.getItem(key) !== raw) throw new Error("Could not persist the support command. Nothing was submitted.");
		return read();
	};
	const ensure = pending => {
		validate(pending);
		const current = read();
		if (!current) return persist(pending.command);
		if (JSON.stringify(current) !== JSON.stringify(pending)) throw new Error("Pending support command changed in another tab.");
		return current;
	};
	const remove = pending => {
		const current = read();
		if (!current) return;
		if (JSON.stringify(current) !== JSON.stringify(pending)) throw new Error("Pending support command changed in another tab.");
		storage.removeItem(key);
		if (storage.getItem(key) !== null) throw new Error("Support confirmation could not be saved. Retry the original request.");
	};
	const confirm = (pending, receipt) => {
		const command = pending.command;
		if (receipt?.success !== true || receipt.projectId !== scope.projectId || receipt.actorUid !== scope.actorUid ||
			receipt.requestId !== command.requestId || receipt.revision !== command.expectedRevision + 1 ||
			!validCaseId(receipt.caseId) || (command.caseId && receipt.caseId !== command.caseId) ||
			!/^support_case_[a-f0-9]{64}$/.test(receipt.auditId || "") || !/^[a-f0-9]{64}$/.test(receipt.payloadHash || "") ||
			typeof receipt.replayed !== "boolean") {
			throw new Error("Support save was not confirmed. Retry the original request.");
		}
		remove(pending);
	};
	const rejectConflict = (pending, error) => {
		const details = error?.details;
		if (error?.code !== "functions/aborted" || details?.supportCaseCommandRejected !== true ||
			details.projectId !== scope.projectId || details.actorUid !== scope.actorUid || details.requestId !== pending.command.requestId) return false;
		remove(pending);
		return true;
	};
	return { read, persist, ensure, confirm, rejectConflict };
}
